// frontend/admin-portal/src/pages/Orders.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  PageHeader,
  Card,
  Button,
  Table,
  Alert,
  formatMoney,
} from "@fiberhome/shared-ui";
import { orderService } from "../services/orderService";
import { useSettings } from "../contexts/SettingsContext";
import OrderStats from "../components/orders/OrderStats";
import OrderFilters from "../components/orders/OrderFilters";
import OrderStatusBadge from "../components/orders/OrderStatusBadge";

export default function Orders() {
  const navigate = useNavigate();
  const { get } = useSettings();
  const currency = get("finance.currencySymbol", "$");

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    search: "",
    kind: "",
    status: "",
    from: "",
    to: "",
  });
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      for (const [k, v] of Object.entries(filters)) {
        if (v !== "" && v != null) params[k] = v;
      }
      const { items, pagination } = await orderService.list(params);
      setItems(items);
      setPagination(pagination);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const columns = [
    {
      key: "code",
      label: "Code",
      render: (r) => (
        <div
          onClick={() => navigate(`/orders/${r.id}`)}
          style={{ cursor: "pointer" }}
        >
          <div style={{ fontWeight: 600 }}>{r.code}</div>
          <div className="text-muted" style={{ fontSize: 11.5 }}>
            {r.kind === "offer" ? "Offer" : "Order"}
          </div>
        </div>
      ),
    },
    {
      key: "customer",
      label: "Customer",
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600 }}>
            {r.customerName || r.customer?.name}
          </div>
          <div className="text-muted" style={{ fontSize: 11.5 }}>
            {r.customerPhone}
          </div>
        </div>
      ),
    },
    { key: "itemCount", label: "Items" },
    {
      key: "totalAmount",
      label: "Total",
      render: (r) => (
        <strong>
          {formatMoney(r.totalAmount, { symbol: r.currency || currency })}
        </strong>
      ),
    },
    {
      key: "issuedAt",
      label: "Issued",
      render: (r) => new Date(r.issuedAt).toLocaleDateString(),
    },
    {
      key: "validUntil",
      label: "Valid until",
      render: (r) => r.validUntil || <span className="text-muted">—</span>,
    },
    {
      key: "status",
      label: "Status",
      render: (r) => <OrderStatusBadge status={r.status} />,
    },
    {
      key: "actions",
      label: "",
      width: 120,
      render: (r) => (
        <Button
          size="sm"
          variant="primary"
          onClick={() => navigate(`/orders/${r.id}`)}
        >
          Open
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Offers & Orders"
        subtitle="Prepare offers for customers and track confirmed orders"
        breadcrumbs={["Home", "Sales", "Offers & Orders"]}
        actions={
          <Button variant="primary" onClick={() => navigate("/orders/new")}>
            + New Offer / Order
          </Button>
        }
      />

      <OrderStats refreshKey={refreshKey} />

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Card className="mb-3">
        <OrderFilters filters={filters} onChange={setFilters} />
      </Card>

      <Card
        title={`${pagination.total} record${pagination.total === 1 ? "" : "s"}`}
        actions={
          <div className="fh-products-toolbar__pager">
            <Button
              size="sm"
              variant="ghost"
              disabled={pagination.page <= 1}
              onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
            >
              Prev
            </Button>
            <span className="text-muted" style={{ fontSize: 12 }}>
              Page {pagination.page} / {pagination.pages || 1}
            </span>
            <Button
              size="sm"
              variant="ghost"
              disabled={pagination.page >= pagination.pages}
              onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
            >
              Next
            </Button>
          </div>
        }
      >
        <Table
          columns={columns}
          data={items}
          loading={loading}
          emptyMessage="No offers or orders yet"
        />
      </Card>
    </>
  );
}
